import React from 'react';
import { GjsAttributeNames, GjsEditorCommands } from './Enums';
import ModalManager from '../../layout/ModalManager';
import AssetPicker from '../../filebrowser/AssetPicker2';
import { AssetTypes, CustomEventTypes } from '../../../globals';
import StepPicker from "../../sequences/StepPicker";
import AssetUtility from '../../../classes/AssetUtility';
import ScenarioPicker from '../../ScenarioPicker';
import CameraPicker from '../../CameraPicker';
import { embeddedImagesBase64 } from '../../../interfaces/VisageUI';
import AssetsLibraryUtilities from '../../filebrowser/AssetsLibraryUtilities';
import SetGrapesComponentDataAction from '../../../actions/visageUI/SetGrapesComponentDataAction';

interface IOpenAssetsCommandOptions
{
	target?: any; // grapes something
	accept?: string; // types of files to accept
	callback?: (assetId: string) => void; // callback to call when asset is selected
	select?: (asset: string, complete: boolean) => void; // native grapes callback
	types?: string[]; // types of files to accept - grapes native
	value?: string, // value to set the picker to
}

interface IOpenStepPickerCommandOptions
{
	stepId?: string; // value to set the picker to
	callback?: (stepId: string) => void; // callback to call when step is selected
}

interface IOpenScenarioPickerCommandOptions
{
	scenarioId?: string; // value to set the picker to
	callback?: (scenarioId: string) => void; // callback to call when scenario is selected
}

interface IOpenCameraPickerCommandOptions
{
	cameraId?: string; // value to set the picker to
	callback?: (cameraId: string) => void; // callback to call when camera is selected
}

export function loadCommands(editor)
{
	// overrides default asset manager
	editor.Commands.add(GjsEditorCommands.OpenAssets, (editor, sender, opts: IOpenAssetsCommandOptions) => 
	{
		switch(opts.accept)
		{
			case 'image/*':
				ModalManager.addModal({
					content:
						<AssetPicker
							type={AssetTypes.Image}
							assetSelectedCallback={(assetId: string) => 
							{
								// callback is defined by us
								if(opts.callback) opts.callback(assetId);

								// select is defined by grapes
								if(opts.select)
								{
									const previewImageSrc = AssetUtility.getPreviewAssetURL(assetId) ?? embeddedImagesBase64.imagePlaceholder;

									// Call the original select function
									if(opts.target) // when both opts.target and opts.select are set, it means the asset picker was opened by double-clicking an image copmonent
									{
										opts.target.setAttributes({ ...opts.target.getAttributes(), [GjsAttributeNames.ImageSource]: previewImageSrc, [GjsAttributeNames.AssetID]: assetId });
										new SetGrapesComponentDataAction({
											actionName: "Set Component Image",
											componentIds: [opts.target.getId()],
											attributeChanges: [
												{
													name: GjsAttributeNames.ImageSource,
													oldValues: [opts.target.getAttributes()[GjsAttributeNames.ImageSource]],
													newValues: [previewImageSrc]
												},
												{
													name: GjsAttributeNames.AssetID,
													oldValues: [opts.target.getAttributes()[GjsAttributeNames.AssetID]],
													newValues: [assetId]
												}
											]
										});
										AssetsLibraryUtilities.refresh();
									}
									else // when only opts.select is set, it means that the asset picker was opened from the grapesjs style manager
									{
										opts.select(previewImageSrc, true);
									}

									document.dispatchEvent(new CustomEvent(CustomEventTypes.grapesImageSet, { detail: { imageId: assetId, component: opts.target } }));
								}
							}}
						/>
				});
				break;
			case "attachment/*":
				ModalManager.addModal({
					content:
						<AssetPicker
							type={[AssetTypes.Video, AssetTypes.Document, AssetTypes.Image]}
							assetSelectedCallback={opts.callback}
						/>
				});
				break;
			case "video/*":
				ModalManager.addModal({
					content:
						<AssetPicker
							type={[AssetTypes.Video]}
							assetSelectedCallback={opts.callback}
						/>
				});
				break;
			case "audio/*":
				ModalManager.addModal({
					content:
						<AssetPicker
							type={[AssetTypes.Audio]}
							assetSelectedCallback={opts.callback}
						/>
				});
				break;
			default:
				console.warn("asset picker opened with unsupported expectation ", opts.accept);
				break;
		}
	});

	// add step picker
	editor.Commands.add(GjsEditorCommands.OpenStepPicker, (editor, sender, opts: IOpenStepPickerCommandOptions) => 
	{
		ModalManager.addModal({
			content:
				<StepPicker
					value={opts.stepId ?? ""}
					onSelectedCallback={opts.callback}
					filterCps={true}
				/> 
		});
	});

	// add scenario picker
	editor.Commands.add(GjsEditorCommands.OpenScenarioPicker, (editor, sender, opts: IOpenScenarioPickerCommandOptions) =>
	{
		ModalManager.addModal({
			content:
				<ScenarioPicker
					value={opts.scenarioId}
					onSelectedCallback={opts.callback}
				/>
		});
	});

	// add camera picker
	editor.Commands.add(GjsEditorCommands.OpenCameraPicker, (editor, sender, opts: IOpenCameraPickerCommandOptions) =>
	{
		ModalManager.addModal({
			content:
				<CameraPicker 
					value={opts.cameraId ?? ""}
					cameraSelectedCallback={opts.callback}
				/>
		});
	});
}